import * as React from 'react';
import { OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import {
	iataWithAdditionalQueryForServices,
	useAdditionalQueryForServices,
	useAdditonalQueryForSeatMap,
	useServicePageProps
} from '../hooks';
import { useUpsaleWarning } from '../../../Checkout/components/Checkout/Upsale/hooks';
import { ServiceType } from '../../../Checkout/components/Checkout/OrderPaidServiceCards/types';
import ServicePage from '../../../Checkout/components/Checkout/ServicePage/ServicePage';
import ServiceCardDialog from '../../../Checkout/components/Checkout/OrderPaidServiceCards/components/ServiceCardDialog/ServiceCardDialog';
import Warning from '../../../Checkout/components/Checkout/Upsale/Warning/Warning';
import { useSelector } from 'react-redux';
import {
	getIsAllSegmentsAvailableOnlyOnCheckin,
	getSeatsMinPrice,
	getServicesFallbackURL,
	shouldRedirectToFallback
} from '../../../Checkout/store/order/selectors';
import { useTranslation } from 'react-i18next';
import { getSeatsPrice } from '../../../Checkout/store/orderPrice/selectors';
import { useTheme } from '../../../theme';
import icons from '../../icons';
import { getPassengerStateCompatible } from '../../../Checkout/store/selectedServices/selectors';
import { ServiceButton } from '../../types';
import { Money } from '../../../types';
import cn from 'classnames';
import { useRenderers } from '../../../renderProps';
import { getSeatsWithDoubleSeats } from '../../../SeatMap/utils';
import { useCart } from '../../../Checkout/components/Checkout/Stepbar/hooks';
import { useIataCode } from '../../../hooks';
import SimpleLoader from '../../../SimpleLoader';
import { ServiceSummary } from '../../../features/service-summary';

export interface Props {
	isCheckinAvailable: boolean;
	aggregationMode?: boolean;
	segmentId?: string;
	minPrice?: Money;
	totalPrice?: Money;
	isSelected?: boolean;
	aggregationIcons?: React.ReactNode[];
	className?: string;
}

const SeatsAdditionalService: React.FC<Props> = ({
	isCheckinAvailable,
	aggregationMode,
	segmentId,
	children,
	minPrice: minPriceProps,
	totalPrice: totalPriceProps,
	isSelected: isSelectedProps,
	aggregationIcons,
	className
}) => {
	const { t } = useTranslation('Checkout');
	const minPrice = aggregationMode ? minPriceProps : useSelector(getSeatsMinPrice);
	const seatsIssuedPrice = aggregationMode ? totalPriceProps : useSelector(getSeatsPrice);

	const servicePageProps = useServicePageProps(OrderAdditionalServiceGdsServiceServiceType.Seat);
	const shouldRedirect = useSelector(shouldRedirectToFallback);
	const servicesRedirectURL = useSelector(getServicesFallbackURL);
	const isAllSegmentsAvailableOnCheckin = useSelector(getIsAllSegmentsAvailableOnlyOnCheckin);
	const { showWarning, closeWarning, toggleWarningIfOrderConfirmed } = useUpsaleWarning();

	const toggleSeatsModal = () => {
		if (iataWithAdditionalQueryForServices.includes(iataCode)) {
			loadAndDispatchAdditionalServices();
		}

		servicePageProps.toggleModal(ServiceType.Seats);
	};

	const handleClick = shouldRedirect ? toggleWarningIfOrderConfirmed : toggleSeatsModal;

	const passengers = useSelector(getPassengerStateCompatible);
	const { Seats: css } = useTheme('CheckoutAdditionalService');
	const iataCode = useIataCode();

	const { onDeleteItem } = useCart();

	const allSeatsConfirmed = passengers.every(passenger => {
		const seatsWithDoubleSeats = getSeatsWithDoubleSeats(passenger.seats);

		return passenger.seats.every(seat => seat.isConfirmed || seatsWithDoubleSeats.has(seat.number));
	});

	const { isLoading: isSeatMapLoading, loadAndDispatch: loadAndDispatchSeatMap } = useAdditonalQueryForSeatMap();

	const {
		isLoading: isAdditionalServicesLoading,
		loadAndDispatch: loadAndDispatchAdditionalServices
	} = useAdditionalQueryForServices({
		onCompleted: () => {
			loadAndDispatchSeatMap();
		}
	});

	const buttons: ServiceButton[] = [
		{
			text: t(allSeatsConfirmed ? 'More details' : 'Change'),
			action: toggleSeatsModal,
			buttonType: 'change'
		}
	];

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	const isDV = useIataCode() === 'DV';
	const isServicesLoading = isAdditionalServicesLoading || isSeatMapLoading;

	return (
		<>
			<ServiceSummary
				serviceType="Seat"
				onDelete={onDeleteItem}
				segmentId={segmentId}
			>
				{content => (
					<ServiceWrapper
						showFromZeroIfZeroPrice={isDV}
						className={cn(css.seats, className)}
						showAddButton={!isAllSegmentsAvailableOnCheckin}
						icon={icons.seatIcon}
						addClick={showWarning ? toggleWarningIfOrderConfirmed : handleClick}
						mode={aggregationMode ? 'aggregator' : 'default'}
						coverClassName={css.seats_aggregator}
						header={t('Seats')}
						description={
							isAllSegmentsAvailableOnCheckin
								? isCheckinAvailable
									? t('Seat selection is available in the online registration service')
									: t(
											'You can select seats using the online registration service after issuing tickets'
									  )
								: t('The top ones get the top seats!')
						}
						priceTotal={seatsIssuedPrice.amount ? seatsIssuedPrice : null}
						buttons={buttons}
						priceFrom={!isAllSegmentsAvailableOnCheckin ? minPrice : null}
						isSelected={!!content}
						aggregationIcons={aggregationIcons}
					>
						{content}
					</ServiceWrapper>
				)}
			</ServiceSummary>

			<Warning open={showWarning} redirectURL={servicesRedirectURL} onClose={closeWarning} />
			{isServicesLoading && <SimpleLoader />}

			<ServicePage>
				{childrenProps => (
					<ServiceCardDialog
						order={servicePageProps.order}
						openedModal={isServicesLoading ? null : servicePageProps.openedModal}
						onClose={servicePageProps.toggleModal.bind(null, null)}
						passengers={servicePageProps.passengers}
						passengerLabels={servicePageProps.passengerLabels}
						segmentId={parseInt(segmentId)}
						{...childrenProps}
					/>
				)}
			</ServicePage>
		</>
	);
};

export default SeatsAdditionalService;
